import { LoadingIcon, LocationMapDialog } from '@/components/controls';
import {
  useDeleteSubject,
  useInsertSubject,
  useSubjects,
  useToggleSubjectKey,
} from '@/hooks';
import { getGroupType, spin, toProperCase } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { HighPrioritySuspect } from './HighPrioritySuspect';
import { PrisonRecall } from './PrisonRecall';
import { Suspect } from './Suspect';
import { useNichePrisonRecalls } from './useNichePrisonRecalls';
import { useNicheSuspects } from './useNicheSuspects';
import { useNicheWarrants } from './useNicheWarrants';
import { Warrant } from './Warrant';

export function Subjects({ title, groupCodes, onComplete }) {
  const {
    query: {
      data: suspects,
      isFetching: isFetchingSuspects,
      refetch: refetchSuspects,
    },
  } = useNicheSuspects(groupCodes);
  const {
    query: {
      data: prisonRecalls,
      isFetching: isFetchingPrisonRecalls,
      refetch: refetchPrisonRecalls,
    },
  } = useNichePrisonRecalls(getGroupType('SECTOR'), groupCodes);
  const {
    query: {
      data: outstandingWarrants,
      isFetching: isFetchingWarrants,
      refetch: refetchWarrants,
    },
  } = useNicheWarrants(getGroupType('SECTOR'), groupCodes);
  const {
    query: {
      data: highPrioritySuspects,
      isFetching: isFetchingHighPrioritySuspects,
      refetch: refetchHighPrioritySuspects,
    },
  } = useSubjects('SUSPECT', getGroupType('SECTOR'), groupCodes, {
    highPriority: true,
  });
  const highPriorityPersons = useMemo(() => {
    const persons = new Map();
    for (const { code, personId, occurrenceId } of highPrioritySuspects ?? []) {
      if (!persons.has(personId)) {
        persons.set(personId, { code, personId, occurrenceId });
      }
    }
    return [...persons.values()];
  }, [highPrioritySuspects]);
  const highPriorityPersonIds = useMemo(
    () => new Set(highPriorityPersons.map(({ personId }) => personId)),
    [highPriorityPersons],
  );
  const outstandingSuspects = useMemo(
    () =>
      suspects.filter(
        (suspect) => !highPriorityPersonIds.has(suspect.subject?.id),
      ),
    [suspects, highPriorityPersonIds],
  );
  const { mutate: insertSubject } = useInsertSubject();
  const { mutate: toggleSubjectKey } = useToggleSubjectKey();
  const { mutate: deleteSubject } = useDeleteSubject();
  const { width, ref } = useResizeDetector();
  const highPriorityColumns = useMemo(
    () => Math.floor(width / 328) || 1,
    [width],
  );
  const lowPriorityColumns = useMemo(
    () => Math.floor(width / 248) || 1,
    [width],
  );
  const [selectedIndex, setSelectedIndex] = useState({
    type: null,
    personId: null,
  });
  const selectedRecord = useMemo(() => {
    switch (selectedIndex.type) {
      case 'SUSPECT':
        return suspects.find(
          ({ subject }) => subject?.id === selectedIndex.personId,
        )?.subject;
      case 'PRISON_RECALL':
        return prisonRecalls.find(
          ({ subject }) => subject?.id === selectedIndex.personId,
        )?.subject;
      case 'WARRANT':
        return outstandingWarrants.find(
          ({ subject }) => subject?.id === selectedIndex.personId,
        )?.subject;
      default:
        return null;
    }
  }, [selectedIndex, suspects, prisonRecalls, outstandingWarrants]);

  function handleRefreshClick() {
    refetchSuspects();
    refetchPrisonRecalls();
    refetchWarrants();
    refetchHighPrioritySuspects();
  }

  const handleShowMapClick = (type, personId) => () => {
    setSelectedIndex({ type, personId });
  };

  function handleMapClose() {
    setSelectedIndex({
      type: null,
      personId: null,
    });
  }

  function handleCompleteClick() {
    onComplete();
  }

  const handleOptionClick = (type, personId, occurrenceId, code, key) => () => {
    if (code) {
      toggleSubjectKey({ code, key });
    } else {
      insertSubject({ type, personId, occurrenceId, groupCodes, keys: [key] });
    }
  };

  const handleDeleteClick = (code) => () => {
    deleteSubject(code);
  };

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton
                onClick={handleRefreshClick}
                disabled={
                  isFetchingSuspects ||
                  isFetchingPrisonRecalls ||
                  isFetchingWarrants ||
                  isFetchingHighPrioritySuspects
                }
              >
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetchingSuspects ||
                    isFetchingPrisonRecalls ||
                    isFetchingWarrants ||
                    isFetchingHighPrioritySuspects
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        {(highPriorityPersons.length > 0 || isFetchingHighPrioritySuspects) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                High Priority Suspects
              </Typography>
              <LoadingIcon isLoading={isFetchingHighPrioritySuspects} />
            </Stack>
            <Masonry spacing={1} columns={highPriorityColumns}>
              {highPriorityPersons.map(({ code, personId, occurrenceId }) => (
                <HighPrioritySuspect
                  key={personId}
                  personId={personId}
                  occurrenceId={occurrenceId}
                  onOptionClick={handleOptionClick}
                  onShowMapClick={handleShowMapClick('SUSPECT', personId)}
                  onDeleteClick={handleDeleteClick(code)}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(outstandingSuspects.length > 0 || isFetchingSuspects) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Outstanding Suspects
              </Typography>
              <LoadingIcon isLoading={isFetchingSuspects} />
            </Stack>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {outstandingSuspects.map((suspect) => (
                <Suspect
                  key={suspect.subject?.id}
                  suspect={suspect}
                  onOptionClick={handleOptionClick}
                  onShowMapClick={handleShowMapClick(
                    'SUSPECT',
                    suspect.subject?.id,
                  )}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(prisonRecalls?.length > 0 || isFetchingPrisonRecalls) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Prison Recalls
              </Typography>
              <LoadingIcon isLoading={isFetchingPrisonRecalls} />
            </Stack>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {prisonRecalls.map((recall) => (
                <PrisonRecall
                  key={recall.id}
                  recall={recall}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(outstandingWarrants?.length > 0 || isFetchingWarrants) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Outstanding Warrants
              </Typography>
              <LoadingIcon isLoading={isFetchingWarrants} />
            </Stack>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {outstandingWarrants.map((warrant, index) => (
                <Warrant
                  key={warrant.subject?.id ?? index}
                  warrant={warrant}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
      </Stack>
      <LocationMapDialog
        addresses={selectedRecord?.addresses}
        title={`Points of interest for ${toProperCase(
          [selectedRecord?.forenames, selectedRecord?.surname]
            .filter(Boolean)
            .join(' '),
        )}`}
        isOpen={!!selectedRecord}
        onClose={handleMapClose}
      />
    </Box>
  );
}
